/* What the Access policy screen states about bundles and versions, decided once and in one place.
 *
 * Three readings live here because each is a conclusion rather than a lookup: whether policy is
 * actually deciding anything for this tenant, which bytes this process is executing, and whether a
 * bundle can be deleted without changing who is allowed. A conclusion assembled inline in a component
 * is one nothing can test.
 */

import type { BundleVersion, LoadedBundle } from '@/shared/api/generated/policy';

/** How much of a hash to show. Enough to compare two by eye; the whole value goes in a `title`.
 *
 * The hash is what proves the process is executing the bytes somebody reviewed, and `content_hash`
 * is verified against the fetched artifact before anything is instantiated precisely because object
 * storage is not trusted to return what was written.
 */
const HASH_VISIBLE = 12;

export function shortHash(hash: string): string {
  return hash.slice(0, HASH_VISIBLE);
}

/** The version a tenant has activated, or `undefined` when none is.
 *
 * `find` rather than a filter and an index, because "the active version" is singular by construction:
 * activating pins one and deprecates the previous. A second active row would be a substrate defect,
 * and this returning the first would hide it — so the caller that cares asserts the count instead.
 */
export function activeVersion(
  versions: readonly BundleVersion[],
): BundleVersion | undefined {
  return versions.find((version) => version.state === 'active');
}

/** The set of hashes this process is executing. A set because every version asks the same question
 * of it, and a nested `some` over the list would be quadratic for no reason. */
export function runningHashesOf(loaded: readonly LoadedBundle[]): ReadonlySet<string> {
  return new Set(loaded.map((bundle) => bundle.content_hash));
}

/* ------------------------------------------------------------ is policy actually deciding anything

 * **The page's thesis, and the thing that was wrong.** This screen reported "Policy runtime
 * available" from `runtime_available`, which means only that the wasmtime wheel is installed — so
 * on a deployment with the wheel present and `LUMIQ_ABAC_ENABLED` unset it announced a capability
 * that decided nothing. A reader had no way to tell an enforcing deployment from an inert one.
 *
 * Five readings, because there are five genuinely different situations and each needs a different
 * action from a different person:
 *
 * * `no-engine` — an operator has to install a wasmtime build for this platform. Nothing a tenant
 *   does can take effect until they have.
 * * `abac-off` — **nobody has to do anything, and that is the correction.** This used to mean "an
 *   operator has forgot `LUMIQ_ABAC_ENABLED`", which made amber right; it now means this tenant
 *   decides access by role, which is a legitimate posture and the one most tenants are in. The copy
 *   distinguishes a *declared* off from one inherited from the deployment default, because those are
 *   different conversations with a customer.
 * * `on-without-policy` — scoping is switched on for this tenant and there is no governing bundle,
 *   so roles decide alone. Deliberate rather than a fault: refusing here would mean that switching
 *   scoping on before authoring a policy — the obvious order — locks every user out until the policy
 *   exists, with the recovery being the screen that has just been taken away. It is `warn` rather
 *   than `info` because it is a state somebody asked for and has not finished.
 * * `failing-closed` — urgent: a policy exists, cannot be honoured, and **every request is being
 *   refused**. An administrator has to publish or reactivate a version.
 * * `enforcing` — the healthy state, and it names the version so a reader can check it.
 *
 * **`no-policy` was a sixth member and is deleted rather than kept.** With scoping off answered
 * before the bundle state, and scoping on answered by `on-without-policy`, nothing could reach it —
 * so it would have read as a mechanism while being unreachable, which this codebase records as worse
 * than its absence.
 *
 * Derived here rather than in the component because it is the conclusion the whole screen exists
 * to state, and a conclusion assembled inline is one nothing can test.
 */
export type EnforcementReading =
  'no-engine' | 'abac-off' | 'on-without-policy' | 'failing-closed' | 'enforcing';

export interface EnforcementSummary {
  readonly reading: EnforcementReading;
  readonly tone: 'ok' | 'warn' | 'error' | 'info';
  readonly titleKey: string;
  readonly bodyKey: string;
}

/** Deployment facts first, then the tenant's own. The order is the point.
 *
 * A tenant with no bundle on a process with no engine has two problems, and naming the engine is
 * the one an operator can act on — so the deployment questions are answered before the tenant's.
 * Reversing it would report "no policy for this tenant" on a deployment where no tenant could have
 * one, which is true and useless.
 */
export function enforcementSummary(state: {
  readonly state: string;
  readonly runtime_available: boolean;
  readonly abac_enabled: boolean;
}): EnforcementSummary {
  if (!state.runtime_available) {
    return {
      reading: 'no-engine',
      tone: 'warn',
      titleKey: 'policy.enf.noEngine.title',
      bodyKey: 'policy.enf.noEngine.body',
    };
  }
  if (!state.abac_enabled) {
    /* **`info`, not `warn`, and this is the change that matters most on the page.** Scoping off used
     * to be a deployment misconfiguration — somebody had forgotten `LUMIQ_ABAC_ENABLED` — so amber
     * was right. It is now a setting a tenant chooses, and "RBAC only" is a legitimate posture that
     * most tenants are in; painting a chosen state as a warning is how a reader learns to ignore
     * this banner. The copy distinguishes a *declared* off from a defaulted one. */
    return {
      reading: 'abac-off',
      tone: 'info',
      titleKey: 'policy.enf.off.title',
      bodyKey: 'policy.enf.off.body',
    };
  }
  if (state.state === 'no_bundle') {
    return {
      reading: 'on-without-policy',
      tone: 'warn',
      titleKey: 'policy.enf.onNoPolicy.title',
      bodyKey: 'policy.enf.onNoPolicy.body',
    };
  }
  if (state.state === 'loaded') {
    return {
      reading: 'enforcing',
      tone: 'ok',
      titleKey: 'policy.enf.enforcing.title',
      bodyKey: 'policy.enf.enforcing.body',
    };
  }
  /* `no_active_version` and `load_failed` share a reading and **not** a sentence: both refuse every
     request, so the urgency is identical, and what an administrator does about it is not. The
     server's own `detail` names which, and the screen renders it verbatim rather than deciding. */
  return {
    reading: 'failing-closed',
    tone: 'error',
    titleKey: 'policy.enf.failing.title',
    bodyKey:
      state.state === 'no_active_version'
        ? 'policy.enf.failing.noVersion'
        : 'policy.enf.failing.loadFailed',
  };
}

/* ---------------------------------------------------------------- can this bundle be deleted

 * **An active bundle is not deletable, and the screen must say so before the button is pressed.**
 *
 * Deleting a bundle whose version is deciding requests would change authorization as a side effect
 * of housekeeping. The API refuses with a 409 and the exact sentence; this is what stops a reader
 * having to discover that by pressing.
 *
 * `undefined` versions is **not** an empty list: it means this screen has not read them yet, and
 * reporting a bundle as deletable on the strength of a pending request would offer a control that is
 * about to be refused.
 */
export type Deletability = 'deletable' | 'blocked-active' | 'unknown';

export function deletabilityOf(
  versions: readonly BundleVersion[] | undefined,
): Deletability {
  if (versions === undefined) return 'unknown';
  return activeVersion(versions) === undefined ? 'deletable' : 'blocked-active';
}
